import * as E from 'fp-ts/Either';
import * as t from 'io-ts';

export const codecFromArray = <T extends string | number>(i: [T, ...T[]]): [t.KeyofC<Record<T, T>>, [T, ...T[]]] => {
  const allKeys = i.reduce((all, key) => ({ ...all, [key]: key }), {} as Record<T, T>);
  return [t.keyof(allKeys), [...i]];
};

class LiteralsType<A, O = A, I = unknown> extends t.Type<A, O, I> {
  constructor(
    public values: readonly [string, ...Array<string>],
    name: string,
    is: t.Is<A>,
    validate: t.Validate<unknown, A>,
    encode: t.Encode<A, O>,
  ) {
    super(name, is, validate, encode);
  }

  addPreprocessor(preprocessor: (i: unknown) => I) {
    return new LiteralsType(
      this.values,
      this.name,
      this.is,
      (i: unknown, context) => this.validate(preprocessor(i), context),
      this.encode,
    );
  }
}

export const iLiteral = <A extends readonly [string, ...Array<string>]>(...values: [string, ...string[]] & A) =>
  new LiteralsType<A[number]>(
    values,
    values.join(' | '),
    (i): i is A[number] => t.string.is(i) && values.includes(i as string),
    (i, context) => {
      const decoded = t.string.decode(i);
      if (E.isLeft(decoded)) {
        return decoded;
      }

      const lowerS = decoded.right.toLowerCase();

      const found = values.find((v) => v.toLowerCase() === lowerS);

      return found !== undefined ? t.success(found) : t.failure(decoded.right, context);
    },
    t.string.encode,
  );
